import { publish } from '@edx/frontend-platform';
import { handleRtl, LOCALE_CHANGED } from '@edx/frontend-platform/i18n';

import { preferencesApi } from '../api';

/**
 * Default language code.
 */
export const DEFAULT_LANGUAGE = 'en';

/**
 * Maps language codes to their respective locale codes.
 * This is a direct mapping between language codes (e.g., 'en') and their corresponding
 * locale codes (e.g., 'en' for English, 'zh-cn' for Chinese).
 *
 * @property {string} en - English (en)
 * @property {string} es-419 - Spanish (es)
 * @property {string} fr - French (fr)
 * @property {string} hi - Hindi (hi)
 * @property {string} sw - Swahili (sw)
 * @property {string} zh-cn - Chinese (zh)
 *
 * @example
 * // Get the French locale code
 * const frenchLocale = LOCALE_MAP.fr; // 'fr'
 *
 * // Get the Chinese locale code
 * const chineseLocale = LOCALE_MAP['zh-cn']; // 'zh'
 */
export const LOCALE_MAP: Record<string, string> = {
  en: 'en',
  'es-419': 'es',
  fr: 'fr',
  hi: 'hi',
  sw: 'sw',
  'zh-cn': 'zh',
};

/**
 * List of language codes that are supported by the site.
 *
 * @property {string} en - English (en)
 * @property {string} es-419 - Spanish (es)
 * @property {string} fr - French (fr)
 * @property {string} hi - Hindi (hi)
 * @property {string} sw - Swahili (sw)
 * @property {string} zh-cn - Chinese (zh)
 *
 * @example
 * // Check if a language is allowed
 * const isAllowed = ALLOWED_SITE_LANGUAGES.includes('en'); // true
 */
export const ALLOWED_SITE_LANGUAGES = Object.keys(LOCALE_MAP);

/**
 * Updates the user's language preference and applies it to the application.
 *
 * This function:
 * - Updates the language preference via the preferences API
 * - Optionally reloads the page to apply changes (if shouldPageReload is true)
 * - Otherwise, updates the UI in-place by publishing a locale change event
 *
 * @param isAuthenticatedUser - Whether the user is currently authenticated
 * @param locale - The locale code to set (e.g., 'en', 'es')
 * @param username - The username for whom to update the preference
 * @param shouldPageReload - Whether to reload the page after updating (default: false)
 *
 * @example
 * // Update language with page reload
 * await updateLanguagePreference(true, 'es', 'user123', true);
 *
 * // Update language without page reload
 * await updateLanguagePreference(true, 'fr', 'user123');
 *
 * @throws {Error} If the API call fails
 * @see {@link preferencesApi.updateLanguagePreference} - For the underlying API call
 * @see {@link publish} - For the event publishing mechanism
 */
export const updateLanguagePreference = async (
  isAuthenticatedUser: boolean,
  locale: string,
  username: string,
  shouldPageReload = false,
): Promise<void> => {
  await preferencesApi.updateLanguagePreference(isAuthenticatedUser, locale, username);
  if (shouldPageReload) {
    window.location.reload();
  } else {
    publish(LOCALE_CHANGED, locale);
    handleRtl();
  }
};
